import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { del, get } from "@/lib/api";
import { savedLine } from "@/lib/documents";
import type { DocumentDetail } from "@/lib/types";
import { ApprovalBanner } from "./ApprovalBanner";
import { CheckList } from "./CheckList";
import { EditorFooter } from "./EditorFooter";
import { SavedInput } from "./Fields";
import { ItemsPanel, type ItemsKind } from "./ItemsPanel";
import { PositionList } from "./PositionList";
import { PreviewPane } from "./PreviewPane";
import { RecipientFields } from "./RecipientFields";
import { SendDialog } from "./SendDialog";
import { TextsSection } from "./TextsSection";
import { documentKey, useDocumentWrites } from "./useDocument";

const shortDate = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

function requiredDay(label: string, save: (day: string) => Promise<unknown>) {
  return (v: string) => (v ? save(v) : Promise.reject(new Error(`${label} fehlt`)));
}

function DeleteDraft({ id, offer, open, onClose }: { id: number; offer: boolean; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      const r = await del<{ status: "done" | "pending" }>(`/api/documents/${id}`);
      onClose();
      if (r.status === "pending") {
        toast.info("Wartet auf Freigabe");
        return;
      }
      toast.success("Entwurf gelöscht");
      navigate(offer ? "/rechnungen?art=angebote" : "/rechnungen?stufe=draft");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Entwurf löschen?</DialogTitle>
          <DialogDescription>{offer ? "Der Entwurf wird gelöscht." : "Die Buchungen und Ausgaben darauf sind danach wieder offen."}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" className="pointer-coarse:h-11" onClick={onClose}>Abbrechen</Button>
          <Button variant="destructive" className="pointer-coarse:h-11" disabled={busy} onClick={remove}>Entwurf löschen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A draft invoice or offer: positions edited in place, saved as you type, with the PDF preview next to it. */
export function DocumentEditor({ detail }: { detail: DocumentDetail }) {
  const doc = detail.document;
  const writes = useDocumentWrites(doc.id);
  const save = writes.save;
  const [hovered, setHovered] = useState<number | null>(null);
  const [focused, setFocused] = useState<number | null>(null);
  const highlighted = hovered ?? focused;
  const [items, setItems] = useState<ItemsKind | null>(null);
  const [editAddress, setEditAddress] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sending, setSending] = useState(false);

  const fromOffer = doc.offerId !== null && doc.positions.some((p) => p.source?.kind === "offer");
  const offer = useQuery({
    queryKey: documentKey(doc.offerId ?? 0),
    queryFn: () => get<DocumentDetail>(`/api/documents/${doc.offerId}`),
    enabled: fromOffer,
  });
  const offerNumber = offer.data?.document.number ?? null;

  const r = doc.recipient;
  const place = [r.zip, r.city].filter(Boolean).join(" ");
  const address = [[r.street, place].filter(Boolean).join(", "), r.customerNumber ? `Kundennummer ${r.customerNumber}` : ""].filter(Boolean).join(" · ");
  const isOffer = doc.type === "offer";

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Link to="/rechnungen" className="inline-flex items-center gap-1 self-start rounded-sm text-[0.8125rem] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11">
            <ChevronLeft aria-hidden className="size-3.5" />Rechnungen
          </Link>
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-2xl font-semibold tracking-[-0.015em]">{doc.clientName}</h1>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">Entwurf</span>
          </div>
          <span className="text-[0.8125rem] text-muted-foreground">{savedLine(detail.nextNumber, detail.lastChange)}</span>
        </div>
        <div className="ml-auto flex gap-2">
          <Button asChild variant="outline" className="pointer-coarse:h-11">
            <a href={`/api/documents/${doc.id}/pdf`} target="_blank" rel="noopener">PDF öffnen</a>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Weitere Aktionen" className="pointer-coarse:size-11"><MoreHorizontal /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(true)}>Entwurf löschen</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ApprovalBanner approvals={detail.approvals} number={detail.nextNumber} />

      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[999_1_600px] flex-col gap-4">
          <section aria-label="Kunde und Daten" className="flex flex-col gap-3 rounded-xl border bg-card p-4">
            <div className="grid grid-cols-[repeat(auto-fit,minmax(10.5rem,1fr))] gap-3">
              <div className="col-span-full flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                <label htmlFor="doc-customer" className="text-[0.8125rem] font-medium">Kunde</label>
                <Input id="doc-customer" readOnly value={r.name || doc.clientName} className="truncate bg-muted/40 pointer-coarse:h-11" />
                <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                  {address && <span className="min-w-0 truncate">{address}</span>}
                  <button
                    type="button"
                    aria-expanded={editAddress}
                    onClick={() => setEditAddress((v) => !v)}
                    className="rounded-sm text-foreground underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11"
                  >
                    Adresse ändern
                  </button>
                </span>
              </div>
              {isOffer ? (
                <>
                  <SavedInput label="Angebotsdatum" type="date" value={doc.date ?? ""} save={requiredDay("Das Angebotsdatum", (date) => save({ date }))} />
                  <SavedInput label="Gültig bis" type="date" value={doc.validUntil ?? ""} save={(v) => save({ validUntil: v || null })} />
                </>
              ) : (
                <>
                  <SavedInput label="Rechnungsdatum" type="date" value={doc.date ?? ""} save={requiredDay("Das Rechnungsdatum", (date) => save({ date }))} />
                  <fieldset className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
                    <legend className="mb-1.5 text-[0.8125rem] font-medium">Leistungszeitraum</legend>
                    <div className="grid grid-cols-2 gap-2">
                      <SavedInput label="von" type="date" className="[&>label]:sr-only" value={doc.periodFrom ?? ""} save={(v) => save({ periodFrom: v || null })} />
                      <SavedInput label="bis" type="date" className="[&>label]:sr-only" value={doc.periodTo ?? ""} save={(v) => save({ periodTo: v || null })} />
                    </div>
                  </fieldset>
                  <SavedInput
                    label="Zahlungsziel"
                    type="number"
                    min={1}
                    inputMode="numeric"
                    value={doc.timeToPay !== null ? String(doc.timeToPay) : ""}
                    save={(v) => {
                      const days = Number(v);
                      return Number.isInteger(days) && days > 0 ? save({ timeToPay: days }) : Promise.reject(new Error("Bitte die Anzahl Tage eingeben"));
                    }}
                    suffix={doc.dueDate ? `Tage · bis ${shortDate(doc.dueDate)}` : "Tage"}
                  />
                </>
              )}
            </div>
            {editAddress && <RecipientFields recipient={r} save={save} />}
          </section>

          <PositionList
            detail={detail}
            writes={writes}
            offerNumber={offerNumber}
            highlighted={highlighted}
            onHover={setHovered}
            onFocusChange={setFocused}
            onOpenItems={setItems}
          />

          <TextsSection doc={doc} save={save} />
        </div>

        <aside aria-label="Vorschau und Prüfung" className="flex w-full min-w-0 max-w-[420px] flex-[1_1_384px] flex-col gap-3 max-[1023px]:mx-auto">
          <PreviewPane id={doc.id} updatedAt={doc.updatedAt} highlighted={highlighted} />
          <CheckList detail={detail} writes={writes} />
        </aside>
      </div>

      <EditorFooter detail={detail} onSend={() => setSending(true)} />
      <ItemsPanel detail={detail} kind={items} onClose={() => setItems(null)} writes={writes} />
      <DeleteDraft id={doc.id} offer={isOffer} open={deleting} onClose={() => setDeleting(false)} />
      {sending && <SendDialog detail={detail} onClose={() => setSending(false)} />}
    </div>
  );
}
